import React, { useEffect, useMemo, useState } from "react";
import {MetricCard} from "./MetricCard";
import { fmtNum, fmtInt, fmtDate, humanInterval } from "../utils/formatting";
import { Spinner } from "./Spinner";

/**
 * MetricsOverview.jsx
 * - JS (no TypeScript)
 * - Tailwind dark theme with cyan & purple accents
 * - Responsive card grid of metrics + key metadata
 *
 * Props:
 *   apiUrl  (string)  -> default: "http://localhost:8000/metricsoverview"
 *   token   (string)  -> Bearer token for Authorization (required)
 *   refreshMs (number)-> optional auto-refresh interval in ms (e.g., 60000)
 */
export default function MetricsOverview({config}) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");

  const apiUrl = `${config?.apiBaseUrl}/metricsoverview`;
  const refreshMs = 60000; // e.g., 60000 for 1 min auto-refresh

  const fetchMetrics = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(apiUrl, {
        headers: {
          accept: "application/json"
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      const data = await res.json();
      setMetrics(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || "Failed to fetch metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // if (refreshMs && Number.isFinite(refreshMs) && refreshMs > 0) {
    //   const id = setInterval(fetchMetrics, refreshMs);
    //   return () => clearInterval(id);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return metrics;
    return metrics.filter((m) => m.name?.toLowerCase().includes(q));
  }, [metrics, query]);

  return (
    <section
      className={[
        "w-full",
        "bg-gradient-to-b from-gray-950 via-gray-950 to-gray-900",
        "text-gray-200 px-4 sm:px-6 md:px-8 py-8 sm:py-12",
        "rounded-2xl border border-white/10",
      ].join(" ")}
    >
      {/* Header */}
      <div className="relative mb-8 sm:mb-10">
        <div className="pointer-events-none absolute inset-x-0 -top-24 h-48 opacity-60 blur-3xl">
          <div className="mx-auto h-full w-3/4 rounded-full bg-gradient-to-r from-cyan-500/30 via-purple-500/30 to-cyan-500/30" />
        </div>

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Available Metrics</h2>
            <p className="mt-2 text-sm text-gray-300">
              Explore available time-series metrics with fresh values and summary stats.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name (e.g., sofr)"
              className={[
                "w-64 max-w-[70vw]",
                "rounded-xl bg-white/5 text-white placeholder-gray-400",
                "border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500/60",
                "px-3 py-2 text-sm",
              ].join(" ")}
            />
            <button
              onClick={fetchMetrics}
              className="rounded-xl px-3 py-2 text-sm font-semibold bg-white/10 hover:bg-white/15 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-300">
          <Spinner />
          <span className="ml-3">Loading metrics…</span>
        </div>
      )}

      {!loading && err && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 p-4">
          <div className="font-semibold">Error</div>
          <div className="text-sm mt-1 break-words">{String(err)}</div>
        </div>
      )}

      {!loading && !err && filtered.length === 0 && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-gray-300">
          No metrics found{query ? ` for “${query}”` : ""}.
        </div>
      )}

      {/* Grid */}
      {!loading && !err && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
          {filtered.map((m) => (
            <MetricCard
              key={m.name}
              name={m.name}
              firstTs={m.first_ts}
              lastTs={m.last_ts}
              latestTs={m.latest_ts}
              description={m.description}
              count={m.count}
              minValue={m.min_value}
              maxValue={m.max_value}
              avgValue={m.avg_value}
              hasLabels={m.has_labels}
              avgIntervalSeconds={m.avg_interval_seconds}
              pointsPerDay={m.points_per_day}
              latestValue={m.latest_value}
              latestLabels={m.latest_labels}
              fmt={{ fmtNum, fmtInt, fmtDate, humanInterval }}
              enableNavigate={true}
              truncateDesc={256}
            />
          ))}
        </div>
      )}
    </section>
  );
}


// function Spinner() {
//   return (
//     <div className="relative inline-flex">
//       <div className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-400/70 border-t-transparent" />
//     </div>
//   );
// }

